/* ──────────────────────────────────────────────────────────────────────────────
   Faz 4 — GİB İzleme  ·  /gib-monitor   (spec §10, kapsam §2.1.1.4)

   "Backoffice üzerinden e-belgelerin gönderim durumları sorgulanıp, hatalı kayıtlar
    takip edilebilir ve gönderimler tekrar tetiklenebilir olmalıdır."

   ÜÇ BLOK — sırası tesadüf değil:
     ① GİB REDDEDİLEN BELGELER — EN ÜSTTE, KIRMIZI. Reddedilen belge RESMÎ DEĞİLDİR
        ama malı ARAÇTAN ÇIKMIŞTIR (iş kuralı 9). Kimse müdahale etmezse mal da belge
        de kayıptır. Aksiyon: Tekrar Gönder · Belgeyi İptal Et · Hata Detayı.
     ② GÖNDERİM BEKLEYENLER — kesildi, GİB dönüşü yok. SÜRE kolonu: uzayan bekleme
        entegrasyon arızasının ilk işaretidir.
     ③ İADE GEÇMİŞİ — c360 liste (sunucu tarafı sayfalama/sıralama + kolon hunileri):
        tarih / plasiyer / cari / tip / ürün / GİB durumu.

   ══ NEDEN ① ve ② GRID DEĞİL ═══════════════════════════════════════════════════
   Bunlar BÖNGEZİLECEK LİSTE değil, KARAR BLOKLARIDIR: sabit, küçük, aksiyonlu iş
   kuyrukları (GET /api/returns/gib-status). Sayfalama/sıralama/huni onlara bir şey
   KATMAZ; "reddedilenler" 2. sayfaya taşarsa uyarı işlevini kaybeder. c360'a çevrilen
   YALNIZ ③'tür — asıl gezilen, filtrelenen, sayfalanan liste odur.

   ⚠ BOZUK İADE DENETİMİ (spec §10): fotoğrafsız VEYA nedeni "diğer" olan bozuk iade
   AYRI FİLTREDEDİR (⚠ çipi). Hurda suistimale açık bir kalemdir — bkz. useReturnAudit.
   ────────────────────────────────────────────────────────────────────────────── */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  createColumnHelper, getCoreRowModel, getPaginationRowModel, getSortedRowModel, useReactTable,
} from '@tanstack/react-table';
import {
  s, authHeaders, Toast, ExcelIcon, toGridSortField, toODataSortField,
} from './listPageStyles';
import { ListPageShell, Pagination, StatusBadge } from './ui';
import { useC360List, C360Table, ColumnChooser } from './c360';
import { usePageMeta } from '../hooks/usePageMeta';
import { usePageActions } from '../contexts/PermissionContext';
import {
  API_BASE, RETURN_STATUS, DATE_PRESETS,
  RETURN_LIST_EXPAND, RETURN_SEARCH_FIELDS,
  returnColumnFilterMeta, supportsDetailFilter,
  buildVendorReturnBaseFilter, mapReturnRow,
  fetchReturnLookups, fetchProductLabels, searchProductOptions, useReturnAudit,
  statusLabel, statusTone, subTypeLabel, subTypeTone, isScrap, gibLabel, gibTone,
  isPosted, isCancellable,
  fmtDate, fmtQty, fmtMoney, fmtWaiting, unwrap, rst,
} from './returnShared';

const lookupLabel = (list, id) => list.find((x) => x.id === id)?.name || '-';

/** Hata detayı / iptal / tekrar gönderim modalı. */
function ActionModal({ row, kind, busy, onClose, onSubmit }) {
  const [reason, setReason] = useState('');
  const isCancel = kind === 'cancel';
  const isDetail = kind === 'detail';
  const canSubmit = (!isCancel || reason.trim().length > 0) && !busy;

  return (
    <div style={s.overlay} onClick={onClose}>
      <div style={{ ...s.modal, maxWidth: 560 }} onClick={(e) => e.stopPropagation()}>
        <div style={s.modalHeader}>
          <h3 style={s.modalTitle}>
            {isDetail ? 'GİB Hata Detayı' : isCancel ? 'Belgeyi İptal Et' : "GİB'e Tekrar Gönder"}
            {' — '}{row.returnNo || '-'}
          </h3>
          <button style={s.closeBtn} onClick={onClose}>×</button>
        </div>
        <div style={s.modalBody}>
          {isDetail ? (
            <>
              <div style={{
                padding: '12px 14px', borderRadius: 10, marginBottom: 12,
                background: 'var(--danger-soft, rgba(226, 68, 92,0.09))',
                border: '1px solid var(--danger, #e2445c)',
              }}>
                <div style={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase', color: 'var(--danger, #e2445c)' }}>
                  GİB Hata Mesajı
                </div>
                <div style={{ fontSize: 13, marginTop: 4 }}>{row.gibErrorMessage || 'Hata mesajı kaydedilmemiş.'}</div>
              </div>
              <div style={{ fontSize: 13, lineHeight: 1.8, color: 'var(--text-secondary, #6b7280)' }}>
                <div><b>Belge:</b> {row.returnNo} · {fmtDate(row.documentDate)} · {subTypeLabel(row.subType)}</div>
                <div><b>Tutar:</b> {fmtMoney(row.netAmount)} · <b>Kalem:</b> {row.lineCount ?? 0}</div>
                <div><b>Deneme:</b> {row.gibRetryCount ?? 0} · <b>e-Belge:</b> {row.eDocumentUuid || '—'}</div>
                {row.stockAlreadyDeducted && (
                  <div style={{ marginTop: 8, color: 'var(--danger, #e2445c)', fontWeight: 700 }}>
                    Bu belgenin malı stoktan DÜŞMÜŞTÜR. GİB reddi stoğu geri almaz.
                  </div>
                )}
              </div>
            </>
          ) : (
            <p style={{ margin: '0 0 12px', fontSize: 13, color: 'var(--text-secondary, #6b7280)', lineHeight: 1.6 }}>
              {isCancel
                ? "İptal, projeksiyon faturasını PASİF'e çeker → stok ve cari etkisi otomatik geri alınır. Belge silinmez; iz kalır. Yalnız Taslak/Gönderildi durumundaki belge iptal edilebilir."
                : 'Belge GİB kuyruğuna geri konur, deneme sayısı artar, önceki hata mesajı temizlenir. STOĞA DOKUNULMAZ — mal zaten araçtan çıkmıştı.'}
            </p>
          )}
          {isCancel && (
            <>
              <label style={s.label}>Gerekçe *</label>
              <textarea
                style={{ ...s.input, minHeight: 72, resize: 'vertical', width: '100%' }}
                value={reason}
                placeholder="Neden iptal ediliyor? (zorunlu — denetim izi)"
                onChange={(e) => setReason(e.target.value)}
              />
              {!reason.trim() && (
                <div style={{ marginTop: 6, fontSize: 12, color: 'var(--danger, #e2445c)' }}>Gerekçe zorunludur.</div>
              )}
            </>
          )}
        </div>
        <div style={s.modalFooter}>
          <button style={s.btnSecondary} onClick={onClose} disabled={busy}>
            {isDetail ? 'Kapat' : 'Vazgeç'}
          </button>
          {!isDetail && (
            <button
              style={{ ...(isCancel ? s.btnDanger : s.btnPrimary), opacity: canSubmit ? 1 : 0.5, cursor: canSubmit ? 'pointer' : 'not-allowed' }}
              disabled={!canSubmit}
              onClick={() => onSubmit(reason.trim())}
            >
              {busy ? 'İşleniyor…' : (isCancel ? 'Belgeyi İptal Et' : 'Tekrar Gönder')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export function GibMonitorPage() {
  const navigate = useNavigate();
  const { canEdit } = usePageActions();
  usePageMeta('GİB İzleme', 'Reddedilen belgeler, gönderim bekleyenler ve iade geçmişi.');

  // ① + ② karar blokları (gib-status ucu — sabit, küçük, aksiyonlu)
  const [gib, setGib] = useState({ items: [], rejectedCount: 0, pendingCount: 0 });

  const [representatives, setRepresentatives] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [productLabels, setProductLabels] = useState({});

  const [toast, setToast] = useState(null);
  const [busy, setBusy] = useState(false);
  const [modal, setModal] = useState(null); // { row, kind }
  const [columnVisibility, setColumnVisibility] = useState({});
  const [selected, setSelected] = useState(null);

  // ③ Sunucu filtreleri (kolon hunilerinin DIŞINDA kalanlar)
  const [datePreset, setDatePreset] = useState('30');
  const [onlySuspicious, setOnlySuspicious] = useState(false);

  const showToast = useCallback((type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3500);
  }, []);

  // ── ③ İADE GEÇMİŞİ — c360 liste ───────────────────────────────────────────
  // Ürün hunisi alt koleksiyon lambda'sı ister; uç desteklemiyorsa huni DÜŞER (bkz. returnShared).
  const [productFilterOk, setProductFilterOk] = useState(null);
  useEffect(() => { supportsDetailFilter().then(setProductFilterOk); }, []);
  const filterMeta = useMemo(() => returnColumnFilterMeta(productFilterOk), [productFilterOk]);

  const baseFilter = useMemo(
    () => buildVendorReturnBaseFilter(datePreset, onlySuspicious ? "ReturnSubType eq 'SCRAP'" : ''),
    [datePreset, onlySuspicious],
  );

  const list = useC360List({
    apiBase: API_BASE,
    baseFilter,
    expand: RETURN_LIST_EXPAND,
    columnFilterMeta: filterMeta,
    searchFields: RETURN_SEARCH_FIELDS,
    defaultSortField: 'DocumentDate',
    defaultSortDir: 'desc',
    onError: (msg) => showToast('error', msg),
  });

  const {
    rows: rawRows, totalCount, loading,
    filterInput, setFilterInput, filterText, applyFilterText,
    columnFilters, setColumnFilter, clearColumnFilter,
    sortField, sortDir, applySort, pageSize, setPageSize, currentPage, setCurrentPage,
    fetchData, exportRows,
  } = list;

  const pageRows = useMemo(() => rawRows.map(mapReturnRow), [rawRows]);
  const { auditMap, suspicious } = useReturnAudit(pageRows);

  // Denetim modunda temiz bozuk iadeler gizlenir; kanıtı henüz inmemiş satır GÖSTERİLİR
  // ("bilmiyoruz" ≠ "temiz" — denetimde bir kaydı sessizce düşürmek en kötü hatadır).
  const visibleRows = useMemo(() => (
    onlySuspicious
      ? pageRows.filter((r) => !auditMap[r.id] || suspicious(r))
      : pageRows
  ), [pageRows, onlySuspicious, auditMap, suspicious]);

  // ── Lookup'lar ────────────────────────────────────────────────────────────
  useEffect(() => {
    fetchReturnLookups().then(({ representatives: r, vendors: v }) => {
      setRepresentatives(r);
      setVendors(v);
    });
  }, []);

  useEffect(() => {
    const ids = pageRows.flatMap((r) => r.productIds).filter((id) => id && !productLabels[id]);
    if (ids.length === 0) return;
    fetchProductLabels(ids).then((m) => {
      if (Object.keys(m).length) setProductLabels((prev) => ({ ...prev, ...m }));
    });
  }, [pageRows, productLabels]);

  // ── ① + ② gib-status ──────────────────────────────────────────────────────
  const loadGib = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/gib-status`, { headers: authHeaders() });
      if (!res.ok) throw new Error('GİB durumu alınamadı');
      const g = unwrap(await res.json());
      setGib({
        items: g?.items || [],
        rejectedCount: g?.rejectedCount || 0,
        pendingCount: g?.pendingCount || 0,
      });
    } catch (e) {
      showToast('error', e.message || 'GİB durumu alınamadı');
    }
  }, [showToast]);

  useEffect(() => { loadGib(); }, [loadGib]);

  const refreshAll = useCallback(() => { fetchData(); loadGib(); }, [fetchData, loadGib]);

  const repName = useCallback((id) => lookupLabel(representatives, id), [representatives]);
  const vendorName = useCallback((id) => lookupLabel(vendors, id), [vendors]);
  const productNames = useCallback(
    (ids) => (ids || []).map((id) => productLabels[id]).filter(Boolean),
    [productLabels],
  );

  const rejected = useMemo(
    () => gib.items.filter((r) => Number(r.status) === RETURN_STATUS.GIB_RED),
    [gib.items],
  );
  const pending = useMemo(
    () => gib.items.filter((r) => Number(r.status) === RETURN_STATUS.GONDERILDI || Number(r.status) === RETURN_STATUS.ISLENDI),
    [gib.items],
  );

  // ── Aksiyonlar ────────────────────────────────────────────────────────────
  const runAction = async (row, kind, reason) => {
    setBusy(true);
    try {
      const path = kind === 'cancel' ? 'cancel' : 'resend-gib';
      const res = await fetch(`${API_BASE}/${row.id}/${path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(kind === 'cancel' ? { reason } : {}),
      });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(body?.message || body?.Message || 'İşlem başarısız');
      showToast('success', kind === 'cancel'
        ? 'Belge iptal edildi — stok ve cari geri alındı.'
        : 'Belge GİB kuyruğuna alındı.');
      setModal(null);
      refreshAll();
    } catch (e) {
      showToast('error', e.message || 'İşlem başarısız');
    } finally {
      setBusy(false);
    }
  };

  // ── ③ Kolonlar ────────────────────────────────────────────────────────────
  const columnHelper = createColumnHelper();
  const columns = useMemo(() => [
    columnHelper.accessor('returnNo', {
      header: 'Belge No', meta: { filterType: 'text' },
      cell: (info) => <span className="c360-erp-chip">{info.getValue() || '-'}</span>,
    }),
    columnHelper.accessor('documentDate', {
      header: 'Tarih', meta: { filterType: 'date' },
      cell: (info) => fmtDate(info.getValue()),
    }),
    columnHelper.display({
      id: 'representative', header: 'Plasiyer', enableSorting: false, meta: { filterType: 'select' },
      cell: ({ row }) => repName(row.original.representativeId),
    }),
    columnHelper.display({
      id: 'vendor', header: 'Cari', enableSorting: false, meta: { filterType: 'select' },
      cell: ({ row }) => vendorName(row.original.vendorId),
    }),
    columnHelper.accessor('returnSubType', {
      header: 'Tip', meta: { filterType: 'select' },
      cell: (info) => (
        <StatusBadge tone={subTypeTone(info.getValue())}>{subTypeLabel(info.getValue())}</StatusBadge>
      ),
    }),
    columnHelper.display({
      id: 'product', header: 'Ürün', enableSorting: false, meta: { filterType: 'asyncSelect' },
      cell: ({ row }) => {
        const names = productNames(row.original.productIds);
        if (!names.length) return <span style={rst.muted}>—</span>;
        return (
          <span title={names.join(', ')}>
            {names[0]}{names.length > 1 ? ` +${names.length - 1}` : ''}
          </span>
        );
      },
    }),
    columnHelper.display({
      id: 'lineCount', header: 'Kalem', enableSorting: false,
      cell: ({ row }) => <span style={rst.num}>{row.original.lineCount ?? 0}</span>,
    }),
    columnHelper.display({
      id: 'totalQuantity', header: 'Miktar', enableSorting: false,
      cell: ({ row }) => fmtQty(row.original.totalQuantity),
    }),
    columnHelper.accessor('netAmount', {
      header: 'Tutar', enableColumnFilter: false,
      cell: (info) => fmtMoney(info.getValue()),
    }),
    columnHelper.display({
      id: 'purchaseInvoices', header: 'İlişkili Alış Fat.', enableSorting: false,
      cell: ({ row }) => {
        const nos = row.original.purchaseInvoiceNos;
        if (!nos.length) return <span style={rst.muted}>—</span>;
        return (
          <span style={rst.mono} title={nos.join(', ')}>
            {nos.slice(0, 2).join(', ')}{nos.length > 2 ? ` +${nos.length - 2}` : ''}
          </span>
        );
      },
    }),
    columnHelper.accessor('status', {
      header: 'GİB', meta: { filterType: 'select' },
      cell: (info) => (
        <StatusBadge tone={statusTone(info.getValue())}>{statusLabel(info.getValue())}</StatusBadge>
      ),
    }),
    // ⚠ Kanıt — fotoğraf sayısı OData'da YOK, GetById'den sayfa bazında iner (bkz. useReturnAudit).
    columnHelper.display({
      id: 'evidence', header: 'Kanıt', enableSorting: false,
      cell: ({ row }) => {
        const r = row.original;
        if (!isScrap(r.returnSubType)) return <span style={rst.muted}>—</span>;
        const a = auditMap[r.id];
        if (!a) return <span style={rst.muted}>…</span>;
        return suspicious(r)
          ? <StatusBadge tone="bad">{a.photoCount ? 'Nedeni belirsiz' : 'Fotoğrafsız'}</StatusBadge>
          : <StatusBadge tone="ok">{a.photoCount} foto</StatusBadge>;
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [repName, vendorName, productNames, auditMap, suspicious]);

  const lookupOptions = useMemo(() => ({
    representative: representatives.map((r) => ({ value: r.id, label: r.name || r.textId })),
    vendor: vendors.map((v) => ({ value: v.id, label: v.name || v.textId })),
  }), [representatives, vendors]);

  const resolveFilterProps = useCallback((columnId, meta) => {
    if (meta.type === 'asyncSelect') {
      return {
        fetchOptions: searchProductOptions,
        resolveLabel: (id) => productLabels[id] || 'ürün',
      };
    }
    return { options: meta.options || lookupOptions[columnId] };
  }, [lookupOptions, productLabels]);

  const sorting = useMemo(
    () => (sortField ? [{ id: toGridSortField(sortField), desc: sortDir === 'desc' }] : []),
    [sortField, sortDir],
  );
  const handleSortingChange = (updater) => {
    const next = typeof updater === 'function' ? updater(sorting) : updater;
    const first = next[0];
    if (!first) { applySort('DocumentDate', 'desc'); return; }
    applySort(toODataSortField(first.id), first.desc ? 'desc' : 'asc');
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const table = useReactTable({
    data: visibleRows,
    columns,
    state: { sorting, columnVisibility, pagination: { pageIndex: currentPage - 1, pageSize } },
    onSortingChange: handleSortingChange,
    onColumnVisibilityChange: setColumnVisibility,
    manualSorting: true,
    manualPagination: true,
    pageCount: totalPages,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableRowSelection: false,
    getRowId: (row) => String(row.id),
  });

  const handleExport = async () => {
    const cols = [
      { headerName: 'Belge No', valueGetter: ({ row }) => row.returnNo || '' },
      { headerName: 'Tarih', valueGetter: ({ row }) => fmtDate(row.documentDate) },
      { headerName: 'Plasiyer', valueGetter: ({ row }) => repName(row.representativeId) },
      { headerName: 'Cari', valueGetter: ({ row }) => vendorName(row.vendorId) },
      { headerName: 'Tip', valueGetter: ({ row }) => subTypeLabel(row.returnSubType) },
      { headerName: 'Kalem', valueGetter: ({ row }) => row.lineCount },
      { headerName: 'Miktar', valueGetter: ({ row }) => row.totalQuantity },
      { headerName: 'Tutar', valueGetter: ({ row }) => Number(row.netAmount || 0) },
      { headerName: 'İlişkili Alış Fat.', valueGetter: ({ row }) => row.purchaseInvoiceNos.join(', ') },
      { headerName: 'GİB Durumu', valueGetter: ({ row }) => gibLabel(row.status) },
      { headerName: 'Deneme', valueGetter: ({ row }) => row.gibRetryCount ?? 0 },
      // Kanıt YALNIZ görünen sayfa için çözülür → dışa aktarımda çözülmemiş satır '?' olur.
      {
        headerName: 'Kanıt',
        valueGetter: ({ row }) => {
          if (!isScrap(row.returnSubType)) return '-';
          const a = auditMap[row.id];
          if (!a) return '?';
          return suspicious(row) ? (a.photoCount ? 'Nedeni belirsiz' : 'Fotoğrafsız') : `${a.photoCount} foto`;
        },
      },
    ];
    try {
      const n = await exportRows(cols, `gib-izleme_${new Date().toISOString().slice(0, 10)}`, mapReturnRow);
      showToast(n ? 'success' : 'error', n ? `${n} kayıt dışa aktarıldı` : 'Dışa aktarılacak veri yok');
    } catch (e) {
      showToast('error', e.message || 'Dışa aktarım başarısız');
    }
  };

  const toolbarNode = (
    <>
      <input
        style={s.filterInput}
        type="text"
        placeholder="Belge no ile ara..."
        value={filterInput}
        onChange={(e) => setFilterInput(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') applyFilterText(filterInput); }}
      />
      {filterText && (
        <button style={s.btnSecondary} onClick={() => { setFilterInput(''); applyFilterText(''); }}>Temizle</button>
      )}
      <select style={rst.filterSelect} value={datePreset}
              onChange={(e) => { setDatePreset(e.target.value); setCurrentPage(1); }}>
        {DATE_PRESETS.map((d) => <option key={d.value} value={d.value}>Tarih: {d.label}</option>)}
      </select>
      {/* ⚠ Bozuk iade denetimi — spec §10: hurda suistimale açık bir kalemdir. */}
      <span style={{ ...rst.chip, ...(onlySuspicious ? rst.chipDanger : null) }} role="button"
            title="Fotoğrafsız VEYA nedeni 'diğer' olan bozuk iadeler"
            onClick={() => { setOnlySuspicious((v) => !v); setCurrentPage(1); }}>
        ⚠ Fotoğrafsız / nedeni belirsiz bozuk iade
      </span>
      <button style={s.btnSecondary} onClick={refreshAll}>Yenile</button>
      <ColumnChooser table={table} />
      <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 6 }}>
        <button type="button" title="Excel'e Aktar" aria-label="Excel'e Aktar" onClick={handleExport}
                style={{ ...s.btnSecondary, width: 38, height: 38, padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
          <ExcelIcon size={16} />
        </button>
      </span>
    </>
  );

  const metaNode = (
    <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
      <StatusBadge tone={rejected.length ? 'bad' : 'ok'}>{rejected.length} GİB reddi</StatusBadge>
      <StatusBadge tone={pending.length ? 'wait' : 'ok'}>{pending.length} gönderim bekliyor</StatusBadge>
    </span>
  );

  const footerNode = (
    <Pagination
      currentPage={currentPage}
      totalCount={totalCount}
      pageSize={pageSize}
      rowCount={visibleRows.length}
      onPageChange={setCurrentPage}
      onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
    />
  );

  return (
    <div style={s.container}>
      <ListPageShell
        title="GİB İzleme"
        subtitle="Reddedilen belge resmî değildir — ama malı araçtan çıkmıştır. Müdahale edilmezse hem mal hem belge kayıptır."
        meta={metaNode}
        toolbar={toolbarNode}
        footer={footerNode}
      >
        {/* ① GİB REDDEDİLEN BELGELER — EN ÜSTTE, KIRMIZI (KARAR BLOĞU, grid değil) */}
        <div style={{ ...rst.blk, ...rst.blkDanger, borderTop: 'none' }}>
          <p style={{ ...rst.blkTitle, ...rst.blkTitleDanger }}>
            ⚠ GİB reddedilen belgeler — mal stoktan düştü, belge resmî değil
            {rejected.length > 0 && <StatusBadge tone="bad">{rejected.length}</StatusBadge>}
          </p>
          {rejected.length === 0 ? (
            <div style={rst.empty}>GİB reddi yok — tüm belgeler kabul edildi ya da kuyrukta.</div>
          ) : (
            <table style={rst.table}>
              <thead>
                <tr>
                  <th style={rst.th}>Belge No</th>
                  <th style={rst.th}>Tarih</th>
                  <th style={rst.th}>Plasiyer</th>
                  <th style={rst.th}>Tip</th>
                  <th style={{ ...rst.th, ...rst.num }}>Tutar</th>
                  <th style={rst.th}>GİB Hata Mesajı</th>
                  <th style={{ ...rst.th, ...rst.num }}>Deneme</th>
                  <th style={{ ...rst.th, textAlign: 'right' }}>Aksiyon</th>
                </tr>
              </thead>
              <tbody>
                {rejected.map((r) => (
                  <tr key={r.id}>
                    <td style={{ ...rst.td, ...rst.mono, cursor: 'pointer' }}
                        onClick={() => navigate(`/vendor-returns/${r.id}`)}>
                      {r.returnNo || '-'}
                    </td>
                    <td style={rst.td}>{fmtDate(r.documentDate)}</td>
                    <td style={rst.td}>{repName(r.representativeId)}</td>
                    <td style={rst.td}><StatusBadge tone={subTypeTone(r.subType)}>{subTypeLabel(r.subType)}</StatusBadge></td>
                    <td style={{ ...rst.td, ...rst.num }}>{fmtMoney(r.netAmount)}</td>
                    <td style={{ ...rst.td, ...rst.danger, whiteSpace: 'normal', maxWidth: 280 }}>
                      {r.gibErrorMessage || 'Hata mesajı kaydedilmemiş'}
                    </td>
                    <td style={{ ...rst.td, ...rst.num }}>{r.gibRetryCount ?? 0}</td>
                    <td style={{ ...rst.td, textAlign: 'right' }}>
                      <span style={{ display: 'inline-flex', gap: 6, justifyContent: 'flex-end' }}>
                        {canEdit && isPosted(r.status) && (
                          <button type="button" style={rst.smallBtn} disabled={busy}
                                  onClick={() => setModal({ row: r, kind: 'resend' })}>
                            Tekrar Gönder
                          </button>
                        )}
                        {canEdit && isCancellable(r.status) && (
                          <button type="button"
                                  style={{ ...rst.smallBtn, color: 'var(--danger, #e2445c)', borderColor: 'var(--danger, #e2445c)' }}
                                  disabled={busy}
                                  onClick={() => setModal({ row: r, kind: 'cancel' })}>
                            Belgeyi İptal Et
                          </button>
                        )}
                        <button type="button" style={rst.smallBtn}
                                onClick={() => setModal({ row: r, kind: 'detail' })}>
                          Hata Detayı
                        </button>
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* ② GÖNDERİM BEKLEYENLER — süre kolonu (KARAR BLOĞU, grid değil) */}
        <div style={rst.blk}>
          <p style={rst.blkTitle}>
            ⏱ Gönderim bekleyenler — kesildi, GİB dönüşü yok
            {pending.length > 0 && <StatusBadge tone="wait">{pending.length}</StatusBadge>}
          </p>
          {pending.length === 0 ? (
            <div style={rst.empty}>GİB dönüşü bekleyen belge yok.</div>
          ) : (
            <table style={rst.table}>
              <thead>
                <tr>
                  <th style={rst.th}>Belge No</th>
                  <th style={rst.th}>Plasiyer</th>
                  <th style={rst.th}>Tip</th>
                  <th style={{ ...rst.th, ...rst.num }}>Tutar</th>
                  <th style={rst.th}>Durum</th>
                  <th style={rst.th}>Bekleme</th>
                  <th style={{ ...rst.th, textAlign: 'right' }}>Aksiyon</th>
                </tr>
              </thead>
              <tbody>
                {pending.map((r) => {
                  // 24 saati aşan bekleme entegrasyon arızasının ilk işaretidir.
                  const stale = Number(r.waitingHours) >= 24;
                  return (
                    <tr key={r.id}>
                      <td style={{ ...rst.td, ...rst.mono, cursor: 'pointer' }}
                          onClick={() => navigate(`/vendor-returns/${r.id}`)}>
                        {r.returnNo || '-'}
                      </td>
                      <td style={rst.td}>{repName(r.representativeId)}</td>
                      <td style={rst.td}><StatusBadge tone={subTypeTone(r.subType)}>{subTypeLabel(r.subType)}</StatusBadge></td>
                      <td style={{ ...rst.td, ...rst.num }}>{fmtMoney(r.netAmount)}</td>
                      <td style={rst.td}><StatusBadge tone={gibTone(r.status)}>{gibLabel(r.status)}</StatusBadge></td>
                      <td style={{ ...rst.td, ...(stale ? rst.danger : null) }}>
                        {r.waitingHours == null ? '-' : fmtWaiting(r.waitingHours)}
                      </td>
                      <td style={{ ...rst.td, textAlign: 'right' }}>
                        {canEdit && (
                          <button type="button" style={rst.smallBtn} disabled={busy}
                                  onClick={() => setModal({ row: r, kind: 'resend' })}>
                            Tekrar Gönder
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* ③ İADE GEÇMİŞİ — c360 (sunucu tarafı sayfalama/sıralama + kolon hunileri) */}
        <div style={rst.blk}>
          <p style={rst.blkTitle}>
            🕘 İade geçmişi · {totalCount} kayıt
            {onlySuspicious && <StatusBadge tone="bad">Şüpheli hurda filtresi açık</StatusBadge>}
          </p>
          {onlySuspicious && (
            <div style={{ fontSize: 12, color: 'var(--text-secondary, #6b7280)', margin: '0 0 8px' }}>
              Denetim modu: sunucu <b>bozuk</b> iadelere daraltır; fotoğraf kanıtı yalnız GÖRÜNEN SAYFA
              için çözülür (backend'de foto sayısı alanı yoktur) → sayfadaki temiz kayıtlar gizlenir,
              sayaç bozuk iade toplamını gösterir.
            </div>
          )}
          <C360Table
            table={table}
            columnFilterMeta={filterMeta}
            columnFilters={columnFilters}
            onApplyFilter={setColumnFilter}
            onClearFilter={clearColumnFilter}
            resolveFilterProps={resolveFilterProps}
            loading={loading}
            selected={selected}
            onRowClick={setSelected}
            onRowDoubleClick={(row) => navigate(`/vendor-returns/${row.id}`)}
            emptyText="Filtreye uyan iade belgesi yok."
          />
        </div>
      </ListPageShell>

      {modal && (
        <ActionModal
          row={modal.row}
          kind={modal.kind}
          busy={busy}
          onClose={() => setModal(null)}
          onSubmit={(reason) => runAction(modal.row, modal.kind, reason)}
        />
      )}

      <Toast toast={toast} />
    </div>
  );
}

export default GibMonitorPage;
